import { useQuery } from '@tanstack/react-query'
import { type PaginationState, type SortingState } from '@tanstack/react-table'

import { fetchWithHeaders } from 'utils'
import { type BrokersBrokerAttentionNeededItem } from 'utils/web_api.schemas'

import { GrapeAPIError, type GrapeAPIErrorType } from './GrapeAPIError'
import { type PaginatedResponse } from './types'

export type NAVv2SingleValueFilters = {
  after_date?: string
  before_date?: string
  coverage_start_date_after?: string
  coverage_start_date_before?: string
  reimbursement_only?: string
  vip_employer?: string
}

export type FilterTypes = 'include' | 'exclude'

// This allows the Record to have optional keys for each filter type above.
type PartialRecord<K extends keyof any, T> = {

  [P in K]?: T;
}

export type NAVv2ArrayValueFilters = {
  carrier_names?: PartialRecord<FilterTypes, string[]>
  submission_methods?: PartialRecord<FilterTypes, string[]>
  brokerage_ids?: PartialRecord<FilterTypes, string[]>
  plan_years?: PartialRecord<FilterTypes, string[]>
  tag_ids?: PartialRecord<FilterTypes, string[]>
  broker_ids?: PartialRecord<FilterTypes, string[]>
  employer_ids?: PartialRecord<FilterTypes, string[]>
  member_ids?: PartialRecord<FilterTypes, string[]>
  enrollment_types?: PartialRecord<FilterTypes, string[]>
  statuses?: PartialRecord<FilterTypes, string[]>
  reasons?: PartialRecord<FilterTypes, string[]>
  enrollment_categories?: PartialRecord<FilterTypes, string[]>
  enrollment_states?: PartialRecord<FilterTypes, string[]>
  employment_statuses?: PartialRecord<FilterTypes, string[]>
}

export type NAVv2Filters = NAVv2SingleValueFilters & NAVv2ArrayValueFilters

export type useGetBrokersMissingPaymentsParams = {
  pagination: PaginationState
  sorting: SortingState
  // TODO: Merge all filters into a single argument. For now leaving them separate to avoid a larger refactor, but TLDR is
  // we don't need to use Tanstack Filters for this argument any longer.
  filters: NAVv2Filters
}

export function getBrokersMissingPaymentsQueryKey ({
  pagination,
  filters,
  sorting,
}: useGetBrokersMissingPaymentsParams) {
  return [
    'brokers',
    'broker_attention_needed_items',
    'pagination',
    pagination,
    'filters',
    filters,
    'sorting',
    sorting,
  ]
}

export async function fetchBrokersMissingPayments ({
  filters,
  pagination,
  sorting,
}: useGetBrokersMissingPaymentsParams): Promise<
  PaginatedResponse<BrokersBrokerAttentionNeededItem>
> {
  const searchParams = new URLSearchParams()

  searchParams.set('page', pagination.pageIndex.toString())
  searchParams.set('items', pagination.pageSize.toString())

  if (sorting.length > 0) {
    const { id, desc } = sorting[0]
    // Tanstack is expecting the column header ID to be 'status'
    const sortKey = id === 'status' ? 'state' : id
    searchParams.set(`sort[${sortKey}]`, desc ? 'desc' : 'asc')
  }

  Object.entries(filters).forEach(([key, value]) => {
    if (typeof value === 'string') {
      if (value) {
        searchParams.append(`filter[${key}]`, value)
      }
    } else {
      const { include = [], exclude = [] } = value
      include.forEach((val) => {
        searchParams.append(`filter[${key}][]`, val)
      })
      exclude.forEach((val) => {
        searchParams.append(`filter[exclude_${key}][]`, val)
      })
    }
  })

  const response = await fetchWithHeaders(
    `/api/web/brokers/broker_attention_needed_items?${searchParams.toString()}`
  )
  if (!response.ok) {
    const errorJson = await response.json()
    throw GrapeAPIError({
      status: response.status,
      message: errorJson.message,
      errors: errorJson.errors,
    })
  }
  return response.json()
}

export async function fetchAllBrokersMissingPayments ({
  filters,
  pageSize = 50,
}: {
  filters: NAVv2Filters
  pageSize?: number
}): Promise<BrokersBrokerAttentionNeededItem[]> {
  let allRows: BrokersBrokerAttentionNeededItem[] = []
  let pageIndex = 1
  let totalPages = 1

  while (pageIndex <= totalPages) {
    const pagination: PaginationState = { pageIndex, pageSize }

    const response: PaginatedResponse<BrokersBrokerAttentionNeededItem> =
      await fetchBrokersMissingPayments({ filters, pagination, sorting: [] })

    allRows = [...allRows, ...response.data]
    totalPages = response.pagination.total_pages
    pageIndex += 1
  }

  return allRows
}

export function useGetBrokersMissingPayments ({
  pagination,
  filters,
  sorting,
}: useGetBrokersMissingPaymentsParams) {
  return useQuery<PaginatedResponse<BrokersBrokerAttentionNeededItem>, GrapeAPIErrorType>({
    queryKey: getBrokersMissingPaymentsQueryKey({
      pagination,
      filters,
      sorting,
    }),
    queryFn: () =>
      fetchBrokersMissingPayments({ pagination, filters, sorting }),
  })
}
